<template>
	<app-page>
		<app-heading :title="isNew ? 'New Listing' : listing.name" />
		<app-form @submit="onSubmit">
			<app-section class="mt-4">
				<app-section-actions>
					<app-button type="submit" small color="success" v-if="isNew">Create</app-button>
					<app-button type="submit" small color="primary" v-else>Save</app-button>
					<app-button type="cancel" small :to="{ name: 'market-listings' }">Cancel</app-button>
				</app-section-actions>
				<div class="title">Basic informations</div>
				<app-row>
					<app-cell cols="12" md="4">
						<input-text
							label="MLS"
							v-model="listing.mls"
							required
							:errors="errors.mls"
						/>
					</app-cell>
					<app-cell cols="12" md="4">
						<input-text
							label="Title"
							v-model="listing.title"
							required
							:errors="errors.title"
						/>
					</app-cell>
					<app-cell cols="12" md="4">
						<input-select
							label="City"
							:items="cities"
							v-model="listing.market_city"
							:errors="errors.market_city"
							@onChange="onCityChange"
						/>
					</app-cell>
					<app-cell cols="12" md="8">
						<input-text
							label="Address*"
							v-model="listing.address"
							required
							:errors="errors.address"
						/>
					</app-cell>
					<app-cell cols="12" md="2">
						<input-text
							label="Longitude"
							v-model="listing.longitude"
							required
							:errors="errors.longitude"
						/>
					</app-cell>
					<app-cell cols="12" md="2">
						<input-text
							label="Latitude"
							v-model="listing.latitude"
							required
							:errors="errors.latitude"
						/>
					</app-cell>
				</app-row>
				<app-divider class="ma-4" />
				<div class="title">Details</div>
				<app-row>
					<app-cell cols="12" md="3">
						<input-date-picker
							label="Published On"
							v-model="listing.date"
							:errors="errors.date"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							label="# Bedrooms"
							type="number"
							v-model="listing.bedrooms"
							:errors="errors.bedrooms"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							label="URL"
							type="url"
							v-model="listing.url"
							:errors="errors.url"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							label="Featured Image (Link)"
							type="url"
							v-model="listing.featured_image"
							:errors="errors.featured_image"
						/>
					</app-cell>
				</app-row>
				<app-row>
					<app-cell cols="12" md="3">
						<input-text
							label="Price"
							type="number"
							v-model="listing.price"
							:errors="errors.price"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							label="Property Type"
							v-model="listing.property_type"
							:errors="errors.property_type"
						/>
					</app-cell>
					<app-cell cols="12" md="6">
						<input-text
							label="Description"
							v-model="listing.description"
							:errors="errors.description"
						/>
					</app-cell>
				</app-row>
				<app-divider class="ma-4" />
				<div class="title">Realtor</div>
				<app-row>
					<app-cell cols="12" md="4">
						<input-text
							label="Realtor name on listing (Use the realtor dropdown if the realtor exists)"
							v-model="listing.realtor_name"
							:errors="errors.realtor_name"
						/>
					</app-cell>
					<app-cell cols="12" md="4">
						<input-text
							label="Brokerage on listing (Use the realtor dropdown if the realtor exists)"
							v-model="listing.brokerage"
							:errors="errors.brokerage"
						/>
					</app-cell>
					<app-cell cols="12" md="4">
						<input-combobox
							label="Realtor"
							:items="realtors"
							v-model="listing.realtor"
							:errors="errors.realtor"
						/>
					</app-cell>
				</app-row>
				<app-divider class="ma-4" />
				<div class="title">Website details</div>
				<app-row>
					<app-cell cols="12" md="4">
						<input-combobox
							v-model="listing.complex"
							label="Complex"
							:items="complexes"
							:disabled="!listing.market_city"
							:errors="errors.complex"
						/>
					</app-cell>
					<app-cell cols="12" md="4">
						<input-combobox
							label="Sponsor"
							:items="sponsors"
							v-model="listing.sponsor"
							:errors="errors.sponsor"
						/>
					</app-cell>
					<app-cell cols="12" md="4">
						<input-combobox
							label="Manager"
							:items="managers"
							v-model="listing.manager"
							:errors="errors.manager"
						/>
					</app-cell>
				</app-row>
				<app-divider class="ma-4" />
				<div class="title">Revenue analysis details</div>
				<app-row>
					<app-cell cols="12" md="6">
						<input-switch
							label="Is validated?"
							v-model="listing.validated"
							:errors="errors.validated"
						/>
					</app-cell>
					<app-cell cols="12" md="6">
						<input-date-picker
							label="Validation date"
							v-model="listing.validated_on"
							:errors="errors.validated_on"
							:disabled="!listing.validated || alreadyValidated"
						/>
					</app-cell>
				</app-row>
				<app-row>
					<app-cell cols="12" md="3">
						<input-combobox
							label="Analysis preset"
							:items="presets"
							v-model="listing.analysis_preset"
							:errors="errors.analysis_preset"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							label="Revenue"
							type="number"
							v-model="listing.revenue"
							:errors="errors.revenue"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							label="Score"
							type="number"
							v-model="listing.score"
							:errors="errors.score"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-text
							label="Occupancy Rate"
							type="number"
							v-model="listing.occupancy_rate"
							:errors="errors.occupancy_rate"
						/>
					</app-cell>
					<app-cell cols="12" md="3">
						<input-switch
							label="Is long term rental?"
							v-model="listing.long_term"
							:errors="errors.long_term"
						/>
					</app-cell>
				</app-row>
			</app-section>
		</app-form>
	</app-page>
</template>

<script>
export default {
	async created() {
		this.$store.dispatch('app/startLoading')
		try {
			if (this.id !== 'new') {
				this.listing = await this.$ravello.request()
					.v(2)
					.to('markets')
					.to(this.marketId)
					.to('listings')
					.to(this.id)
					.get()

				if (this.listing.market_city) {
					await Promise.all([this.loadComplexes(), this.loadPresets()])
				}

				if (this.listing.validated && this.listing.validated_on) {
					this.alreadyValidated = true
				}
			}

			const [datasources, managers, sponsors] = await Promise.all([
				this.$ravello.graphql('cities')
					.parent(this.$ravello.graphql('market', this.marketId))
					.datasource()
					.sort('name')
					.union(this.$ravello.graphql('realtors')
						.parent(this.$ravello.graphql('market', this.marketId))
						.sort('name')
						.datasource())
					.query(true),
				this.$ravello.graphql('marketmanagers')
					.datasource()
					.sort('name')
					.query(),
				this.$ravello.graphql('marketsponsors')
					.datasource()
					.sort('name')
					.query(),
			])

			this.managers = managers
			this.sponsors = sponsors
			this.realtors = datasources.realtors
			this.cities = datasources.cities
		} catch (err) {
			this.$notifications.pushAlert('error', err.data.message)
		}
		this.$store.dispatch('app/stopLoading')
	},
	data() {
		return {
			listing: {
				title: '',
				address: '',
				longitude: '',
				latitude: '',
				mls: '',
				market_city: null,
				complex: null,
				date: null,
				bedrooms: '',
				url: '',
				featured_image: '',
				price: '',
				property_type: '',
				description: '',
				realtor_name: '',
				brokerage: '',
				realtor: null,
				manager: null,
				sponsor: null,
				analysis_preset: null,
				long_term: false,
				validated: false,
				validated_on: null,
				score: '',
				revenue: '',
				occupancy_rate: '',
			},
			alreadyValidated: false,
			cities: [],
			complexes: [],
			managers: [],
			sponsors: [],
			realtors: [],
			presets: [],
			errors: {},
		}
	},
	computed: {
		marketId() {
			return this.$route.params.market
		},
		id() {
			return this.$route.params.listing
		},
		isNew() {
			return this.id === 'new'
		},
	},
	methods: {
		async onSubmit() {
			this.$store.dispatch('app/startLoading')

			const request = this.$ravello.request()
				.v(2)
				.to('markets')
				.to(this.marketId)
				.to('listings')

			const payload = {
				title: this.listing.title,
				address: this.listing.address,
				longitude: this.listing.longitude,
				latitude: this.listing.latitude,
				mls: this.listing.mls,
				market_city: this.listing.market_city,
				complex: this.listing.complex,
				date: this.listing.date,
				bedrooms: this.listing.bedrooms,
				url: this.listing.url,
				featured_image: this.listing.featured_image,
				price: this.listing.price,
				property_type: this.listing.property_type,
				description: this.listing.description,
				realtor_name: this.listing.realtor_name,
				brokerage: this.listing.brokerage,
				realtor: this.listing.realtor,
				manager: this.listing.manager,
				sponsor: this.listing.sponsor,
				analysis_preset: this.listing.analysis_preset,
				long_term: this.listing.long_term,
				...(!this.alreadyValidated ? {
					validated: this.listing.validated,
					validated_on: this.listing.validated_on,
				} : {}),
				...(this.listing.score && this.listing.score !== '' ? { score: this.listing.score } : {}),
				...(this.listing.revenue && this.listing.revenue !== '' ? { revenue: this.listing.revenue } : {}),
				...(this.listing.occupancy_rate && this.listing.occupancy_rate !== ''
					? { occupancy_rate: this.listing.occupancy_rate } : {}
				),
			}

			try {
				if (this.isNew) {
					await request.create(payload)
				} else {
					await request
						.to(this.id)
						.update(payload)
				}
				this.$store.dispatch('app/stopLoading')
				this.$notifications.pushAlert('success', `Listing successfully ${this.isNew ? 'added' : 'updated'}`)
				this.$router.push({ name: 'market-listings' })
			} catch (err) {
				this.$notifications.pushAlert('error', err.data.message)
				if (err.status === 400) {
					this.errors = err.data.details || {}
				}
				this.$store.dispatch('app/stopLoading')
			}
		},
		async loadComplexes() {
			this.complexes = await this.$ravello.graphql('complexes')
				.parent(this.$ravello.graphql('market', this.marketId))
				.filter('market_city', this.listing.market_city)
				.datasource()
				.sort('name')
				.query()
		},
		async loadPresets() {
			this.presets = await this.$ravello.graphql('analysispresets')
				.parent(this.$ravello.graphql('market', this.marketId))
				.filter('market_city', this.listing.market_city)
				.datasource()
				.sort('name')
				.query()
		},
		onCityChange() {
			this.loadComplexes()
			this.loadPresets()
		},
	},
}
</script>
